import {
  TbAlertTriangle,
  TbCaretDown,
  TbCaretUp,
  TbCircleCheckFilled,
  TbFileText,
  TbLoader2,
  TbTrash,
  TbX,
} from "solid-icons/tb";
import { useAppUI } from "./AppUIContext";
import { StButton } from "@storytell/ui/buttons";
import { useWire } from "~/wire";
import { createSignal, For, Match, onMount, Show, Switch } from "solid-js";
import {
  type ColumnDef,
  createSolidTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Header,
  type SortingState,
} from "@tanstack/solid-table";
import type { ConceptRecord } from "~/stores/sync/syncConcepts";
import { Checkbox } from "@kobalte/core/checkbox";
import { TbCheck } from "solid-icons/tb";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
dayjs.extend(localizedFormat);

export const NavigationConceptsPane = () => {
  const ui = useAppUI();

  return (
    <div class="flex flex-col h-full px-4 py-3 space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-medium">Storytell has extracted these Concepts from your Assets</h2>
      <a href="https://go.storytell.ai/learn-concepts" target="_blank" rel="noreferrer" class="text-brand underline">
          Learn more
        </a>
        <StButton onClick={() => ui.actions.closeSecondaryPane()} icon={TbX} />
      </div>

      <ConceptsTable />
    </div>
  );
};

const ConceptsTable = () => {
  const wire = useWire();

  // Search state
  const [search, setSearch] = createSignal("");

  // Default sorting: by scope column, descending (in-scope first)
  const [sorting, setSorting] = createSignal<SortingState>([]);

  // Bulk selection state
  const [selected, setSelected] = createSignal<Record<string, boolean>>({});

  // Expanded rows state
  const [expandedRows, setExpandedRows] = createSignal<Record<string, boolean>>({});

  // Helper to get all visible concept IDs (first 10 rows)
  const allSelected = () => concepts().every((a) => selected()[a.id]);
  const someSelected = () => concepts().some((a) => selected()[a.id]);

  // Toggle all visible checkboxes
  function toggleAll(checked: boolean) {
    const ids = concepts().map((a) => a.id);
    setSelected((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = checked;
      });
      return next;
    });
  }

  // Sorting based on initial scope to bring the in-scope concepts to the top
  // but also prevent them from shifting around as the user adds/removes concepts
  const concepts = () => {
    if (search()) {
      return wire.sync.concepts.fuzzy
        .search(search())
        .map((result) => wire.sync.concepts.get(result.item.id))
        .filter(Boolean) as ConceptRecord[];
    }
    return wire.sync.concepts.getAll();
  };

  // Table columns definition
  const columns: ColumnDef<ConceptRecord>[] = [
    // {
    //   id: "bulk-select",
    //   header: () => (
    //     <Checkbox
    //       checked={allSelected()}
    //       indeterminate={someSelected() && !allSelected()}
    //       aria-label={allSelected() ? "Deselect all" : "Select all"}
    //       onChange={toggleAll}
    //       class="flex items-center justify-center"
    //     >
    //       <Checkbox.Input />
    //       <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
    //         <Show when={allSelected() || (someSelected() && !allSelected())}>
    //           <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
    //         </Show>
    //       </Checkbox.Control>
    //     </Checkbox>
    //   ),
    //   cell: (info) => {
    //     const id = () => info.row.original.id;
    //     const checked = () => !!selected()[id()];
    //     return (
    //       <Checkbox
    //         checked={checked()}
    //         aria-label={checked() ? "Deselect concept" : "Select concept"}
    //         onChange={(value) => {
    //           setSelected((prev) => ({ ...prev, [id()]: value }));
    //         }}
    //         class="flex items-center justify-center"
    //       >
    //         <Checkbox.Input />
    //         <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
    //           <Show when={checked()}>
    //             <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
    //           </Show>
    //         </Checkbox.Control>
    //       </Checkbox>
    //     );
    //   },
    //   footer: (props) => props.column.id,
    //   enableSorting: false,
    // },
    {
      header: "Name",
      accessorFn: (row) => row.name,
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => <span class="whitespace-nowrap font-medium text-primary-text">{String(info.getValue())}</span>,
      footer: (props) => props.column.id,
    },
    {
      header: "Description",
      cell: (info) => {
        const conceptId = () => info.row.original.id;
        const isExpanded = () => !!expandedRows()[conceptId()];
        let ref!: HTMLDivElement;
        const [clamped, setClamped] = createSignal(false);

        onMount(() => {
          if (ref.scrollHeight > ref.clientHeight) {
            setClamped(true);
          }
        });
        return (
          <div class="space-y-2 relative" classList={{ "overflow-hidden max-h-18": !isExpanded() }} ref={ref}>
            <p class="text-xs font-medium text-primary-text">{info.row.original.summary}</p>
            <p class="text-xs">{info.row.original.description}</p>
            <Show when={clamped() && !isExpanded()}>
              <div class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-panel group-hover/row:from-tile-hover transition-colors to-transparent" />
            </Show>
          </div>
        );
      },
      footer: (props) => props.column.id,
    },
    {
      header: "Last Updated",
      accessorFn: (row) => row.modifiedAt,
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => {
        const date = () => {
          const ts = info.getValue() as { seconds?: number; nanos?: number } | undefined;
          if (!ts || typeof ts !== "object" || ts.seconds == null) return "-";
          // Convert protobuf Timestamp to JS Date
          const date = dayjs.unix(Number(ts.seconds));
          return date.format("LLL");
        };
        return <span class="text-xs">{date()}</span>;
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return concepts();
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<ConceptRecord, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  let inputRef!: HTMLInputElement;
  onMount(() => {
    inputRef.focus();
  });

  return (
    <>
      <div class="mb-3 flex items-center gap-4">
        <input
          type="text"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search Concepts..."
          class="w-full px-3 py-2 border border-primary-border rounded bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          aria-label="Search concepts"
          ref={inputRef}
        />
      </div>

      <div class="rounded-lg border border-primary-border overflow-auto flex-auto mb-2">
        <table class="min-w-full max-w-full text-sm overflow-auto">
          <thead class="sticky top-0 bg-canvas border-b border-primary-border">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <tr>
                  <For each={headerGroup.headers}>
                    {(header) => (
                      <th
                        colSpan={header.colSpan}
                        class="text-xs px-2 py-2 font-medium text-left text-primary-text whitespace-nowrap"
                      >
                        <Show when={!header.isPlaceholder}>
                          <div
                            class={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center gap-1"
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            role={header.column.getCanSort() ? "button" : undefined}
                            aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Show when={header.column.getIsSorted() === "asc"}>
                              <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                            </Show>
                            <Show when={header.column.getIsSorted() === "desc"}>
                              <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                            </Show>
                          </div>
                        </Show>
                      </th>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </thead>
          <tbody>
            <Show
              when={table.getRowModel().rows.length > 0}
              fallback={
                <tr>
                  <td colSpan={columns.length} class="text-center p-4 text-muted-text">
                    No concepts found
                  </td>
                </tr>
              }
            >
              <For each={table.getRowModel().rows}>
                {(row) => {
                  const conceptId = () => row.original.id;
                  const isExpanded = () => !!expandedRows()[conceptId()];
                  return (
                    <tr 
                      class="group/row hover:bg-tile-hover transition-colors cursor-pointer"
                      onClick={() => {
                        setExpandedRows(prev => ({
                          ...prev,
                          [conceptId()]: !isExpanded()
                        }));
                      }}
                    >
                      <For each={row.getVisibleCells()}>
                        {(cell) => (
                          <td
                            class="px-3 py-3 [&:not:last-child]:border-b border-muted-border text-secondary-text"
                            classList={{
                              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                              "align-top": (cell.column.columnDef.meta as any)?.verticalAlign === "top",
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )}
                      </For>
                    </tr>
                  );
                }}
              </For>
            </Show>
          </tbody>
        </table>
      </div>
    </>
  );
};
